import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Briefcase, MapPin, Building2, ExternalLink, Clock, Crown, BadgePoundSterling } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { SEOPage } from '@/components/SEO';
import { ShareMenu } from '@/components/ShareMenu';
import api, { getActiveFeaturedJobs } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const JOBS_FALLBACK_IMG = 'https://customer-assets.emergentagent.com/job_e6bd1d26-d4b0-49be-b226-7ca558f827f9/artifacts/9bhssqo2_ChatGPT%20Image%20Jun%2016%2C%202026%2C%2005_04_33%20PM.png';
const JOBS_FALLBACK_ALT = 'Jobs in Paisley, Renfrewshire & Glasgow';

const FeaturedJobCard = ({ job }) => (
  <Card
    className="relative border-2 border-amber-400/80 shadow-lg shadow-amber-500/10 bg-gradient-to-br from-amber-50/60 via-white to-white overflow-hidden"
    data-testid={`featured-job-card-${job.id}`}
  >
    <div className="absolute top-0 right-0 z-10">
      <div className="bg-gradient-to-r from-amber-500 to-yellow-600 text-white text-[11px] font-semibold uppercase tracking-wide px-3 py-1 rounded-bl-lg flex items-center gap-1 shadow">
        <Crown className="h-3 w-3" /> Featured
      </div>
    </div>
    <CardContent className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        {job.image_file_id && (
          <img
            src={`${BACKEND_URL}/api/files/${job.image_file_id}`}
            alt={`${job.employer} — ${job.title}`}
            onError={(e) => {
              if (e.target.src !== JOBS_FALLBACK_IMG) e.target.src = JOBS_FALLBACK_IMG;
            }}
            className="w-16 h-16 rounded-lg object-cover border border-amber-100 flex-shrink-0"
            loading="lazy"
          />
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2 pr-20">
            {job.title}
          </h3>
          <div className="flex flex-wrap gap-3 text-sm text-slate-600 mb-3">
            <span className="flex items-center gap-1">
              <Building2 className="h-4 w-4 text-amber-500" /> {job.employer}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4 text-amber-500" /> {job.location}
            </span>
            {job.salary && (
              <span className="flex items-center gap-1 font-medium text-amber-700">
                <BadgePoundSterling className="h-4 w-4" /> {job.salary}
              </span>
            )}
          </div>
          {job.description && (
            <p className="text-slate-600 text-sm mb-4">{job.description}</p>
          )}
          <div className="flex flex-col sm:flex-row gap-2 items-start">
            <a href={job.link} target="_blank" rel="noopener noreferrer">
              <Button
                className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white whitespace-nowrap"
                data-testid={`featured-apply-btn-${job.id}`}
              >
                Apply Now <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
            </a>
            <div className="sm:ml-auto">
              <ShareMenu
                title={`${job.title} — ${job.employer}`}
                description={`Featured job opportunity in ${job.location}`}
                url={job.link}
                testId={`share-featured-${job.id}`}
              />
            </div>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const JobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [jobsRes, featRes] = await Promise.all([
          api.get('/jobs'),
          getActiveFeaturedJobs().catch(() => ({ data: [] })),
        ]);
        setJobs(jobsRes.data || []);
        setFeatured(featRes.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  // ItemList JSON-LD — helps Google index the whole jobs board as an indexable collection
  const itemListSchema = jobs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'name': 'Jobs in Paisley & Renfrewshire',
    'description': `${jobs.length} live job vacancies in Paisley, Renfrewshire and Glasgow.`,
    'numberOfItems': jobs.length,
    'itemListElement': jobs.slice(0, 50).map((j, i) => ({
      '@type': 'ListItem',
      'position': i + 1,
      'url': `https://paisleyscotland.com/jobs/${j.slug || j.id}`,
      'name': j.title,
    })),
  } : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="jobs-page">
      <SEOPage.Jobs />
      <Helmet>
        {/* Preload the branded fallback so it appears instantly on slow connections */}
        <link rel="preload" as="image" href={JOBS_FALLBACK_IMG} />
        {/* RSS feed discoverable by aggregators */}
        <link rel="alternate" type="application/rss+xml" title="Paisley Scotland — Latest Jobs" href="https://paisleyscotland.com/api/feed/jobs.xml" />
        {itemListSchema && (
          <script type="application/ld+json">
            {JSON.stringify(itemListSchema)}
          </script>
        )}
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-indigo-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Badge className="bg-white/20 text-white border-0 mb-4">
            <Briefcase className="h-3 w-3 mr-1" /> Paisley&apos;s Dedicated Jobs Board
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4">
            Jobs in Paisley &amp; Renfrewshire
          </h1>
          <p className="text-indigo-100 text-lg max-w-2xl mx-auto">
            The definitive local jobs board for Paisley, Renfrewshire &amp; the surrounding Glasgow area.
            Updated daily from local employers, council, NHS and our sister site paisley.org.uk.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/jobs/post">
              <Button className="bg-white text-indigo-700 hover:bg-indigo-50 shadow-lg font-semibold" data-testid="hero-post-job-cta">
                <Briefcase className="h-4 w-4 mr-2" /> Post a Job — £15
              </Button>
            </Link>
            <Link to="/featured-job">
              <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white hover:text-amber-700" data-testid="hero-feature-job-cta">
                <Crown className="h-4 w-4 mr-2" /> Feature your job — £30
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Jobs List */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          {loading ? (
            <div className="space-y-4">
              {[1,2,3].map(i => (
                <Card key={i} className="animate-pulse"><CardContent className="p-6"><div className="h-5 bg-slate-200 rounded w-3/4 mb-3"/><div className="h-4 bg-slate-100 rounded w-1/2"/></CardContent></Card>
              ))}
            </div>
          ) : (jobs.length > 0 || featured.length > 0) ? (
            <div className="space-y-4">
              {featured.map(f => <FeaturedJobCard key={f.id} job={f} />)}

              {jobs.map(job => {
                const cardImage = job.cover_image || job.image_url || JOBS_FALLBACK_IMG;
                const isFallback = !job.cover_image && !job.image_url;
                return (
                <Card key={job.id} className="hover:shadow-md transition-shadow overflow-hidden" data-testid={`job-card-${job.id}`}>
                  <div className="relative h-40 sm:h-48 bg-slate-100 overflow-hidden">
                    <img
                      src={cardImage}
                      alt={isFallback ? JOBS_FALLBACK_ALT : `${job.title} — ${job.employer}`}
                      loading="lazy"
                      onError={(e) => {
                        if (e.target.src !== JOBS_FALLBACK_IMG) e.target.src = JOBS_FALLBACK_IMG;
                      }}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                          {job.slug ? (
                            <Link to={`/jobs/${job.slug}`} className="hover:text-indigo-600 transition-colors" data-testid={`job-detail-link-${job.id}`}>
                              {job.title}
                            </Link>
                          ) : job.title}
                        </h3>
                        <div className="flex flex-wrap gap-3 text-sm text-slate-500 mb-3">
                          <span className="flex items-center gap-1">
                            <Building2 className="h-4 w-4" /> {job.employer}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" /> {job.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" /> Posted {job.date_posted}
                          </span>
                        </div>
                        {job.description && (
                          <p className="text-slate-600 text-sm">{job.description}</p>
                        )}
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        {job.slug && (
                          <Link to={`/jobs/${job.slug}`}>
                            <Button variant="outline" className="whitespace-nowrap" data-testid={`view-job-btn-${job.id}`}>
                              View details
                            </Button>
                          </Link>
                        )}
                        {job.link && (
                          <a href={job.link} target="_blank" rel="noopener noreferrer">
                            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white whitespace-nowrap" data-testid={`apply-btn-${job.id}`}>
                              Apply <ExternalLink className="h-4 w-4 ml-2" />
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-100 flex justify-end">
                      <ShareMenu
                        title={`${job.title} — ${job.employer}`}
                        description={`Now hiring in ${job.location}`}
                        url={job.slug ? `https://paisleyscotland.com/jobs/${job.slug}` : (job.link || 'https://paisleyscotland.com/jobs')}
                        testId={`share-job-${job.id}`}
                      />
                    </div>
                  </CardContent>
                </Card>
              );})}

              {/* Promo card at bottom */}
              <Card className="border-dashed border-amber-300 bg-amber-50/40">
                <CardContent className="p-6 flex flex-col sm:flex-row items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Crown className="h-6 w-6 text-amber-600" />
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <h3 className="font-semibold text-slate-900 mb-1">Want maximum visibility?</h3>
                    <p className="text-sm text-slate-600">
                      Pin your job to slot #1 with a gold border for 30 days — just £30.
                    </p>
                  </div>
                  <Link to="/featured-job">
                    <Button className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white whitespace-nowrap" data-testid="bottom-feature-job-cta">
                      Feature my job <Crown className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-16">
              <Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No jobs listed right now</h3>
              <p className="text-slate-500 mb-6">Check back soon — we update daily.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link to="/jobs/post">
                  <Button className="bg-gradient-to-r from-sky-600 to-indigo-700 hover:from-sky-700 hover:to-indigo-800 text-white shadow-lg" data-testid="empty-post-job-cta">
                    <Briefcase className="h-4 w-4 mr-2" /> Post a Job — £15
                  </Button>
                </Link>
                <Link to="/featured-job">
                  <Button variant="outline" data-testid="empty-feature-job-cta">
                    <Crown className="h-4 w-4 mr-2" /> Feature your job — £30
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JobsPage;
